declare const config
const FALLBACK_LOCALE = config.fallback_locale

import { DateTime, Duration } from 'luxon'

import { SkinDate } from '@skinsoft/types'
import { DATE_UNIT, getDateFormattedAsInTryphon } from './tryphon-date-format-helper'

export const DATE_FORMATS = {
    DD_MM_YYYY_SLASH: 'dd/MM/yyyy',
    MM_DD_YYYY_SLASH: 'MM/dd/yyyy',
    DD_MM_YYYY_SLASH_HMS: 'dd/MM/yyyy hh:mm:ss',
    DD_MM_YYYY_DASH: 'dd-MM-yyyy',
    MM_DD_YYYY_DASH: 'MM-dd-yyyy',
    DD_MM_YYYY_DASH_HMS: 'dd-MM-yyyy hh:mm:ss',
    DD_MM_YYYY_DASH_HMS_ONE_BLOCK: 'dd-MM-yyyy-hh-mm-ss',
    YYYY_MM_DD_DASH: 'yyyy-MM-dd',
    YYYY_MM_DD_SLASH: 'yyyy/MM/dd',
}

export const DEFAULT_DATE_FORMAT = DATE_FORMATS.YYYY_MM_DD_DASH

export const toFormat = (skinDate: SkinDate, format: string): string => {
    try{
        return DateTime.fromMillis(skinDate.startInterval?.date).toFormat(format)
    } catch {
        return ''
    }
}

export const toTimestamp = (dateText: string, format: string) => DateTime.fromFormat(dateText, format).toMillis()

export function getReadableDate(date: string) {
    if (date && typeof date === "string") { // TODO do better when last date of cr is repaired in backend
        return date.split(" ")[0]
    }
    return ''
}

export function getCurrentDateAsSkinDate() {
    return buildSkinDate(DateTime.now())
}

export function buildSkinDate(dateTime: DateTime, useTimeZone: boolean = true) {
    if(!useTimeZone) {
        // Dates in API (SkinDate) don't use timezones, but they use the number of milliseconds in UTC
        dateTime = dateTime.setZone("utc", { keepLocalTime: true }) 
    }
    const dateText = dateTime.toFormat(DATE_FORMATS.DD_MM_YYYY_SLASH)
    return ({
        dateFIAF: "",
        useDate: "DATE",
        empty: false,
        endInterval: null,
        startInterval: {
            date: dateTime.toMillis(),
            unit: "day",
            dateTxt: dateText
        },
        txt: dateText
    }) as SkinDate
}

export function buildSkinDateWithFormat(dateTxt: string, dateFormat: string): SkinDate {
    const dt = DateTime.fromFormat(dateTxt, dateFormat)
    return buildSkinDate(dt)
}

export function buildSkinDateFromTimeStamp(timeStamp: number) {
    return buildSkinDate(DateTime.fromMillis(timeStamp))
}

export function formatDuration(duration, minutesLabel, secondsLabel) {
    let result = "";
    if (duration) {
        const formattedDuration: Duration = Duration.fromMillis(duration, {}).rescale()
        const minutes = formattedDuration.get('minutes')
        const seconds = formattedDuration.get('seconds')
        if (minutes) {
            result += minutes + " " + minutesLabel + " "
        }
        if (seconds) {
            result += seconds + " " + secondsLabel
        }
    }
    return result
}

export function skinDateToInputDate(date: SkinDate) {
    if (date.startInterval?.date) {
        return toFormat(date, DATE_FORMATS.YYYY_MM_DD_DASH)
    }
    return "";
}

export function formatDateFromNow(timeStamp?: number | null, locale: string = FALLBACK_LOCALE) {
    if (timeStamp) {
        return DateTime.fromMillis(timeStamp).toRelative({ locale })
    }
    return ""
}

export function formatCurrentDate(dateFormat: string): string {
    return DateTime.now().toFormat(dateFormat)
}

/**
 * Returns -1 if dateA < dateB, 1 if dateA > dateB, 0 if dates are equal
 * @param dateA 
 * @param dateB 
 */
export function compareSkinDate(dateA: SkinDate, dateB: SkinDate): Number {
    let intervalToUSeA: any
    let intervalToUSeB: any

    if(dateA.endInterval?.date) {
        intervalToUSeA = dateA.endInterval
    } 
    else if(dateA.startInterval?.date) {
        intervalToUSeA = dateA.startInterval
    }

    if(dateB.endInterval?.date) {
        intervalToUSeB = dateB.endInterval
    } 
    else if(dateB.startInterval?.date) {
        intervalToUSeB = dateB.startInterval
    }

    if(intervalToUSeA?.date && intervalToUSeB?.date) {
        if(intervalToUSeA.date < intervalToUSeB.date) return -1
        else if(intervalToUSeA.date > intervalToUSeB.date) return 1
        else return 0
    }
    
    return 0
}

export function getServerDateFormat(locale: string, unit: string = DATE_UNIT.DAY) {
    let format = DEFAULT_DATE_FORMAT

    // we store the date format from server in localstorage after login (see @skinsoft/auth-ui/login/index.ts)
    const serverDateFormat = localStorage.getItem('dateFormat')

    if(serverDateFormat) {
        format = getDateFormattedAsInTryphon(serverDateFormat, unit.toLowerCase(), locale)
    } else {
        format = getDateFormattedAsInTryphon(DEFAULT_DATE_FORMAT, unit.toLocaleLowerCase(), locale)
    }
    
    return cleanDateFormat(format ?? DEFAULT_DATE_FORMAT)
}

export function cleanDateFormat(dateFormat: string): string {
    // adapt date format to luxon
    let cleanedDateFormat = dateFormat
    // @ts-ignore : until update of typescript replaceAll will generate a linting error
    cleanedDateFormat = dateFormat.replaceAll('m', 'M')
    // @ts-ignore : until update of typescript replaceAll will generate a linting error
    cleanedDateFormat = cleanedDateFormat.replaceAll('Y', 'y')
    // @ts-ignore : until update of typescript replaceAll will generate a linting error
    cleanedDateFormat = cleanedDateFormat.replaceAll('D', 'd')

    return cleanedDateFormat
}